import { Link } from 'react-router-dom';

const chapters = [
  {
    tag: '第1章',
    title: '総則',
    desc: '本規約の適用範囲および定義について定めています。',
    variant: 'dark' as const,
  },
  {
    tag: '第2章',
    title: 'サービス利用',
    desc: '当社サービスの利用条件、禁止事項、免責事項について定めています。',
    variant: 'image' as const,
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20photography%20of%20a%20modern%20Japanese%20office%20conference%20room%20with%20a%20large%20screen%20displaying%20data%2C%20clean%20minimalist%20design%2C%20dark%20wood%20table%2C%20professional%20setting%2C%20no%20people%2C%20corporate%20atmosphere&width=600&height=800&seq=terms-service-chapter&orientation=portrait',
  },
  {
    tag: '第3章',
    title: '禁止事項',
    desc: '法令違反行為、第三者への迷惑行為、当社システムへの不正アクセス等を禁止しています。',
    variant: 'text' as const,
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20photography%20of%20a%20clean%20modern%20Japanese%20office%20desk%20with%20contract%20documents%20and%20a%20fountain%20pen%2C%20minimalist%20composition%2C%20warm%20lighting%2C%20legal%20aesthetic%2C%20no%20people%2C%20professional%20setting&width=640&height=360&seq=terms-prohibited&orientation=landscape',
  },
];

export default function Terms() {
  return (
    <>
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-primary-900 via-primary-800 to-primary-900 pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="inline-flex items-center gap-2 mb-8">
            <span className="text-accent-400 text-sm tracking-[0.2em] font-sans font-light">/</span>
            <span className="text-accent-400/80 text-xs md:text-sm tracking-[0.15em] font-sans font-light">法的文書</span>
          </div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-light text-white tracking-[0.06em]">
            利用規約
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-200/70 tracking-[0.06em] leading-[1.9] font-sans font-light max-w-xl">
            本規約は、当社サービスをご利用いただく際の条件を定めるものです。サービスをご利用になる前に、必ずご確認ください。
          </p>
        </div>
      </section>

      {/* Chapter Grid */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <p className="text-xs md:text-sm text-foreground-400 tracking-[0.15em] font-sans font-light mb-2">BANKCARD CHECK</p>
          <h3 className="text-xl md:text-3xl lg:text-4xl font-heading font-light text-foreground-950 tracking-[0.06em] mb-12">
            規約の概要
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {/* Chapter 1 - Dark */}
            <div className="group relative rounded-lg overflow-hidden bg-primary-900 p-8 md:p-10 flex flex-col justify-between min-h-[300px] md:min-h-[360px] cursor-pointer">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-foreground-700/30 flex items-center justify-center">
                <span className="text-accent-400 text-sm md:text-base font-heading tracking-[0.1em]">{chapters[0].tag}</span>
              </div>
              <div>
                <h4 className="text-xl md:text-2xl text-white tracking-[0.06em] font-heading font-light leading-relaxed">{chapters[0].title}</h4>
                <p className="mt-3 text-xs md:text-sm text-foreground-400 tracking-[0.06em] font-sans font-light">{chapters[0].desc}</p>
              </div>
            </div>

            {/* Chapter 2 - Image */}
            <div className="group relative rounded-lg overflow-hidden min-h-[300px] md:min-h-[360px] cursor-pointer">
              <img src={chapters[1].imageUrl} alt="サービス利用" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-primary-900/50" />
              <div className="absolute top-4 left-4">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-900/70 backdrop-blur-sm text-accent-400 text-[11px] md:text-xs tracking-[0.1em] font-sans font-light">{chapters[1].tag}</span>
              </div>
              <div className="absolute bottom-8 left-8 right-8">
                <h4 className="text-xl md:text-2xl text-white tracking-[0.06em] font-heading font-light leading-relaxed">{chapters[1].title}</h4>
                <p className="mt-3 text-xs md:text-sm text-white/70 tracking-[0.06em] font-sans font-light">{chapters[1].desc}</p>
              </div>
            </div>

            {/* Chapter 3 - Text */}
            <div className="group relative rounded-lg overflow-hidden bg-background-100 p-8 md:p-10 flex flex-col justify-between min-h-[300px] md:min-h-[360px] cursor-pointer">
              <h4 className="text-xl md:text-2xl text-foreground-950 tracking-[0.06em] font-heading font-light leading-relaxed">{chapters[2].title}</h4>
              <div>
                <p className="text-xs md:text-sm text-foreground-500 tracking-[0.06em] font-sans font-light mb-4">{chapters[2].desc}</p>
                <div className="w-full aspect-[16/9] rounded-md overflow-hidden bg-background-200">
                  <img src={chapters[2].imageUrl} alt="禁止事項" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Long Text Content */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-background-100">
        <div className="max-w-[960px] mx-auto">
          <div className="rounded-lg bg-background-50 p-8 md:p-12 lg:p-16 space-y-10">
            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第1条（適用）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                本利用規約（以下「本規約」といいます。）は、株式会社バンクカード・チェック（以下「当社」といいます。）が提供するすべてのサービス（以下「本サービス」といいます。）の利用条件を定めるものです。本サービスをご利用になるお客様（以下「ユーザー」といいます。）は、本規約に同意したものとみなされます。
              </p>
            </article>

            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第2条（利用登録）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                本サービスの利用を希望する方は、当社所定の方法により利用登録を申請するものとします。当社は、申請内容を審査し、登録を承認した場合に、ユーザーに対してアカウントを発行します。
              </p>
            </article>

            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第3条（禁止事項）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                ユーザーは、本サービスの利用にあたり、以下の行為を行ってはなりません。<br />
                1. 法令または公序良俗に違反する行為<br />
                2. 当社または第三者の知的財産権を侵害する行為<br />
                3. 当社のサーバーまたはネットワークに不正にアクセスする行為<br />
                4. 本サービスの運営を妨害する行為<br />
                5. 他のユーザーに関する個人情報を収集または蓄積する行為<br />
                6. その他、当社が不適切と判断する行為
              </p>
            </article>

            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第4条（免責事項）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                当社は、本サービスに起因してユーザーに生じたあらゆる損害について、当社の故意または重過失による場合を除き、一切の責任を負いません。また、当社は、本サービスの提供の中断、停止、終了によって生じたいかなる損害についても責任を負いません。
              </p>
            </article>

            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第5条（サービス内容の変更等）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                当社は、ユーザーに事前通知することなく、本サービスの内容を変更し、または本サービスの提供を中止することができるものとします。
              </p>
            </article>

            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第6条（利用規約の変更）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                当社は、必要と判断した場合には、ユーザーに通知することなく本規約を変更することができるものとします。変更後の規約は、当ウェブサイトに掲載した時点から効力を生じるものとします。
              </p>
            </article>

            <article>
              <h3 className="text-lg md:text-xl text-foreground-950 tracking-[0.06em] font-sans font-medium mb-4">第7条（準拠法および管轄裁判所）</h3>
              <p className="text-sm md:text-base text-foreground-600 tracking-[0.05em] leading-[1.9] font-sans font-light">
                本規約の解釈にあたっては、日本法を準拠法とします。本サービスに関して紛争が生じた場合には、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
              </p>
            </article>

            <p className="text-xs text-foreground-400 tracking-[0.06em] font-sans font-light pt-4">
              制定日：2015年4月1日<br />
              最終改定日：2026年4月1日
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-light text-foreground-950 tracking-[0.06em]">
            規約に関するご質問
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-500 tracking-[0.08em] font-sans font-light">
            ご不明な点がございましたら、お気軽にお問合わせください
          </p>
          <div className="mt-6">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary-800 text-accent-400 text-sm md:text-base tracking-[0.08em] font-sans font-medium hover:bg-primary-700 transition-all duration-300 cursor-pointer whitespace-nowrap">
              お問合わせ
              <i className="ri-arrow-right-line text-lg" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}